import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useEffect, useState } from 'react';
import { CardComponent } from '../../../common/cardComponent';
import { getProjectByName } from '../../../../utils/mergeData';
import SelectComponent from '../../../common/selectComponent';
import DownloadCardComponent from '../../../common/downloadCardComponent';

// ─── helpers ────────────────────────────────────────────────────────────────

// Map to store consistent mock IDs for species-name sensor IDs
const sensorIdMockMap = new Map<string, string>();
let camMockCounter = 1;
let audMockCounter = 1;

/**
 * Detects if a sensor ID is actually a species name (e.g. "Brown Shrike", "root")
 * and returns a proper mock sensor ID instead.
 */
const normalizeSensorId = (rawId: string | null | undefined, type: 'Camera' | 'Audio'): string => {
  if (!rawId) {
    return type === 'Camera' ? `CAM-${String(camMockCounter++).padStart(4, '0')}` : `AUD-${String(audMockCounter++).padStart(4, '0')}`;
  }
  const isRealSensorId = /^[0-9A-Za-z]{1,8}$/.test(rawId) && !/\s/.test(rawId) && rawId !== 'root' && rawId !== 'null';
  if (isRealSensorId) {
    return rawId;
  }
  const cacheKey = `${type}-${rawId}`;
  if (!sensorIdMockMap.has(cacheKey)) {
    const mockId = type === 'Camera'
      ? `CAM-${String(camMockCounter++).padStart(4, '0')}`
      : `AUD-${String(audMockCounter++).padStart(4, '0')}`;
    sensorIdMockMap.set(cacheKey, mockId);
  }
  return sensorIdMockMap.get(cacheKey)!;
};

/**
 * Formats a sensor ID into a display label with type prefix (e.g., "Camera-0011" or "Audio-0021")
 */
const formatSensorDisplayLabel = (id: string, type: 'Camera' | 'Audio'): string => {
  if (!id) return type === 'Camera' ? 'Camera-0001' : 'Audio-0001';
  if (id.startsWith('Camera-') || id.startsWith('Audio-')) {
    return id;
  }
  if (id.startsWith('CAM-')) {
    return `Camera-${id.replace('CAM-', '')}`;
  }
  if (id.startsWith('AUD-')) {
    return `Audio-${id.replace('AUD-', '')}`;
  }
  const prefix = type === 'Camera' ? 'Camera' : 'Audio';
  return `${prefix}-${id}`;
};

/** Build a flat array of display items from one year-bucket in species_detected */
const buildDataFromYear = (yearData: any, year: string): any[] => {
  if (!yearData) return [];
  const baseURL  = yearData.base_url  ?? '';
  const camDir   = yearData.img_dir   ?? '';
  const audDir   = yearData.audio_dir ?? '';
  const clean    = (p: string) => encodeURI(p.replace(/(?<!https?:)\/+/g, '/'));

  const images = (yearData.image_species ?? []).map((item: any) => {
    let url: string | null = null;
    if (item.camera_id === 'root') {
      url = clean(`${baseURL}/${camDir}/${item.Name}`);
    } else if (item.camera_id) {
      if (item.Type) {
        const cleanType = item.Type.replace(/\s*\([^)]*\)/g, '').trim();
        const speciesFolder = (item.camera_id === '0611' || item.camera_id === '0612')
          ? cleanType.toLowerCase()
          : cleanType;
        url = clean(`${baseURL}/${camDir}/${item.camera_id}/${speciesFolder}/${item.Name}`);
      } else {
        url = clean(`${baseURL}/${camDir}/${item.camera_id}/${item.Name}`);
      }
    }
    const sensorId = normalizeSensorId(item.camera_id, 'Camera');
    return {
      id:           sensorId,
      rawId:        item.camera_id,
      type:         item.Type,
      device:       'Camera',
      sensorDevice: 'Camera',
      date:         item.Date,
      year,
      score:        item.Confidence_Score,
      name:         item.Name,
      url,
    };
  });

  const audio = (yearData.audio_species ?? []).map((item: any) => {
    let url: string | null = null;
    if (item.audiomoth_id === 'root') {
      url = clean(`${baseURL}/${audDir}/${item.Name}`);
    } else if (item.audiomoth_id) {
      url = clean(`${baseURL}/${audDir}/${item.audiomoth_id}/${item.Name}`);
    }
    const sensorId = normalizeSensorId(item.audiomoth_id, 'Audio');
    return {
      id:           sensorId,
      rawId:        item.audiomoth_id,
      type:         item.Type,
      device:       'Audio',
      sensorDevice: 'Audio',
      date:         item.Date,
      year,
      score:        item.Confidence_Score,
      name:         item.Name,
      url,
    };
  });

  return [...images, ...audio];
};

/** Month-name options derived from a list of data items */
const monthOptionsFromItems = (items: any[]) =>
  Array.from(new Set(items.map((i: any) => i.date?.split('-')[1]).filter(Boolean)))
    .sort()
    .map((num: string) =>
      new Date(2024, parseInt(num, 10) - 1).toLocaleString('default', { month: 'long' })
    );

/** Unique sensor options derived from a list of data items */
const sensorOptionsFromItems = (items: any[]) => {
  const seen = new Set<string>();
  return items
    .filter((i: any) => i.id && !seen.has(i.id) && seen.add(i.id))
    .map((i: any) => ({
      label: formatSensorDisplayLabel(i.id, i.device as 'Camera' | 'Audio'),
      value: i.id
    }));
};

/** Month number (1-12) from a month name string */
const monthNameToNum = (name: string) =>
  new Date(Date.parse(`${name} 1, 2024`)).getMonth() + 1;

// ─── component ───────────────────────────────────────────────────────────────
const BiodiversityDownloadFilter = () => {
  // Read stable primitive keys from localStorage (strings → safe as useEffect deps)
  const projectName: string =
    (() => {
      try { return JSON.parse(localStorage.getItem('selectedProject') || '{}')?.project_name ?? ''; }
      catch { return ''; }
    })();

  const siteName: string =
    (() => {
      try { return JSON.parse(localStorage.getItem('SelectedDropDownSite') || '{}')?.site_name ?? ''; }
      catch { return ''; }
    })();

  // ── state ──────────────────────────────────────────────────────────────────
  const [loader]                              = useState(false);
  const [cardTitle, setCardTitle]             = useState('Species Data');
  const [allData,   setAllData]               = useState<any[]>([]);   // full set (all years)
  const [data,      setData]                  = useState<any[]>([]);   // currently displayed

  const [selectedYear,    setSelectedYear]    = useState<string>('');
  const [selectedMonth,   setSelectedMonth]   = useState<string>('');
  const [selectedSensor,  setSelectedSensor]  = useState<string[]>([]);
  const [selectedSpecies, setSelectedSpecies] = useState<string>('');

  const [yearOptions,    setYearOptions]    = useState<string[]>([]);
  const [monthOptions,   setMonthOptions]   = useState<string[]>([]);
  const [sensorOptions,  setSensorOptions]  = useState<any[]>([]);
  const [speciesOptions, setSpeciesOptions] = useState<any[]>([]);

  const [checkboxStates, setCheckboxStates] = useState<boolean[]>([]);
  const [enabled,        setEnabled]        = useState(false);
  const [selectedData,   setSelectedData]   = useState<any[]>([]);

  // ── sync checkboxes when displayed data changes ────────────────────────────
  useEffect(() => {
    setCheckboxStates(new Array(data.length).fill(false));
  }, [data]);

  // ── INIT: load ALL years combined whenever project/site changes ────────────
  useEffect(() => {
    const project    = getProjectByName(projectName);
    const site       = project?.sites?.find((s: any) => s.site_name === siteName);
    const speciesData = site?.species_detected;

    if (!speciesData?.length) return;

    const years = Object.keys(speciesData[0]);
    setYearOptions(years);

    // Combine data from every year
    const combined: any[] = years.flatMap(year => buildDataFromYear(speciesData[0][year], year));

    setAllData(combined);
    setData(combined);
    setMonthOptions(monthOptionsFromItems(combined));
    setSensorOptions(sensorOptionsFromItems(combined)); // show all sensors by default

    // Reset filter selections
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedSensor([]);
    setSelectedSpecies('');
    setSpeciesOptions([]);
  }, [projectName, siteName]); // ← stable string values, not object/array refs

  // ── YEAR change ─────────────────────────────────────────────────────────────
  const handleYearChange = (event: any) => {
    const year = event.target.value as string;
    setSelectedYear(year);

    // Reset dependent filters
    setSelectedMonth('');
    setSelectedSensor([]);
    setSelectedSpecies('');
    setSpeciesOptions([]);

    const yearFiltered = year ? allData.filter(i => i.year === year) : allData;
    setMonthOptions(monthOptionsFromItems(yearFiltered));
    setSensorOptions(sensorOptionsFromItems(yearFiltered));
    setData(yearFiltered);
  };

  // ── MONTH change ────────────────────────────────────────────────────────────
  const handleMonthChange = (event: any) => {
    const month = event.target.value as string;
    setSelectedMonth(month);

    // Reset dependent filters
    setSelectedSensor([]);
    setSelectedSpecies('');
    setSpeciesOptions([]);

    const yearFiltered = selectedYear ? allData.filter(i => i.year === selectedYear) : allData;

    // Always derive sensors from the relevant scope (month or full year)
    if (month) {
      const monthNum   = monthNameToNum(month);
      const monthItems = yearFiltered.filter(i => parseInt(i.date?.split('-')[1], 10) === monthNum);
      setSensorOptions(sensorOptionsFromItems(monthItems));
    } else {
      // month cleared → show all sensors for current year scope
      setSensorOptions(sensorOptionsFromItems(yearFiltered));
    }
  };

  // ── SENSOR change ───────────────────────────────────────────────────────────
  const handleSensorSelect = (event: any) => {
    const sensorIds = event.target.value as string[];
    setSelectedSensor(sensorIds);
    setSelectedSpecies('');
    setSpeciesOptions([]);

    if (sensorIds.length > 0) {
      const yearFiltered = selectedYear ? allData.filter(i => i.year === selectedYear) : allData;
      let filtered       = yearFiltered.filter(i => sensorIds.includes(i.id));

      if (selectedMonth) {
        const monthNum = monthNameToNum(selectedMonth);
        filtered = filtered.filter(i => parseInt(i.date?.split('-')[1], 10) === monthNum);
      }

      const uniqueSpecies = Array.from(new Set(filtered.map((i: any) => i.type))).map(t => ({
        label: t as string,
        value: t as string,
      }));
      setSpeciesOptions(uniqueSpecies);
    }
  };

  // ── APPLY ───────────────────────────────────────────────────────────────────
  const handleFilter = () => {
    let filtered = [...allData];

    if (selectedYear) {
      filtered = filtered.filter(i => i.year === selectedYear);
    }
    if (selectedMonth) {
      const monthNum = monthNameToNum(selectedMonth);
      filtered = filtered.filter(i => parseInt(i.date?.split('-')[1], 10) === monthNum);
    }
    if (selectedSensor.length > 0) {
      filtered = filtered.filter(i => selectedSensor.includes(i.id));
    }
    if (selectedSpecies) {
      filtered = filtered.filter(i => i.type === selectedSpecies);
    }

    setCardTitle(filtered.length > 0 ? 'Filtered Data' : 'Species Data');
    setData(filtered);
  };

  // ── RESET ───────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedSensor([]);
    setSelectedSpecies('');
    setSensorOptions([]);
    setSpeciesOptions([]);
    setMonthOptions(monthOptionsFromItems(allData));
    setData(allData);
    setCardTitle('Species Data');
  };

  // ── checkbox / download ─────────────────────────────────────────────────────
  const handleCheckboxChange = (index: number) => {
    const updated = [...checkboxStates];
    updated[index] = !updated[index];
    setCheckboxStates(updated);
    const selectedRows = data.filter((_, i) => updated[i]);
    setEnabled(selectedRows.length > 0);
    setSelectedData(selectedRows);
  };

  const handleDownload = (selectedItems: any[]) => {
    const blob = new Blob([JSON.stringify(selectedItems, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'selected-data.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── year dropdown options – first entry is "All Years" ──────────────────
  const yearSelectOptions = [
    { label: 'All Years', value: '' },
    ...yearOptions.map(y => ({ label: y, value: y })),
  ];

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ mt: 5 }}>
      <Backdrop
        sx={{ color: '#005F54', zIndex: theme => theme.zIndex.drawer + 1 }}
        open={loader}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <CardComponent title={'Filter Species Data'}>
        <Grid container spacing={2} sx={{ m: 2 }}>
          {/* Year */}
          <Grid size={{ lg: 4, sm: 6 }}>
            <SelectComponent
              label="Year"
              options={yearSelectOptions}
              value={selectedYear}
              onChange={handleYearChange}
              displayEmpty
            />
          </Grid>

          {/* Month */}
          <Grid size={{ lg: 4, sm: 6 }}>
            <SelectComponent
              label="Month"
              options={monthOptions.map(m => ({ label: m, value: m }))}
              value={selectedMonth}
              onChange={handleMonthChange}
              displayEmpty
            />
          </Grid>

          {/* Sensor */}
          <Grid size={{ lg: 4, sm: 6 }}>
            <SelectComponent
              label="Sensor"
              options={sensorOptions}
              value={selectedSensor}
              onChange={handleSensorSelect}
              multiple
              displayEmpty
            />
          </Grid>

          {/* Species */}
          <Grid size={{ lg: 4, sm: 6 }}>
            <SelectComponent
              label="Species"
              options={speciesOptions}
              value={selectedSpecies}
              onChange={e => setSelectedSpecies(e.target.value)}
              displayEmpty
            />
          </Grid>
        </Grid>

        <Box sx={{ borderTop: '1px solid #c4c4c4', m: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            variant="outlined"
            sx={{ m: 1, borderColor: '#005F54', color: '#005F54' }}
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            sx={{ m: 1, backgroundColor: '#005F54', color: '#fff' }}
            disabled={loader}
            onClick={handleFilter}
          >
            {loader ? 'Processing' : 'Apply'}
          </Button>
        </Box>
      </CardComponent>

      <Box sx={{ m: 1 }}>
        {loader ? (
          <Typography>Loading...</Typography>
        ) : data && data.length > 0 ? (
          <DownloadCardComponent
            title={cardTitle}
            btnName="Download Selected Data"
            handleButtonEvent={handleDownload}
            data={data.map((i: any) => i.type)}
            dataId={data.map((i: any) => i.name)}
            checkboxes={data.map(() => 'Check')}
            dateTitle={data.map(() => 'Date')}
            date={data.map((i: any) => i.date)}
            scoreTitle={data.map(() => 'Score')}
            score={data.map((i: any) => i.score)}
            url={data.map((i: any) => i.url)}
          />
        ) : (
          <Typography
            sx={{ fontSize: '20px', fontWeight: 700, textAlign: 'center', margin: 3 }}
          >
            No Species found for this filter
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default BiodiversityDownloadFilter;
